import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sparkles,
  ShieldCheck,
  Lock,
  Megaphone,
  TrendingUp,
  Users,
  Mail,
  Zap,
  BadgeCheck,
  Store,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";
import { toast } from "sonner";

const APP_OPTIONS: { value: string; label: string }[] = [
  { value: "fryqnc", label: "Fryqnc" },
  { value: "pulsynq", label: "Pulsynq" },
  { value: "mindlynq", label: "MindLynq" },
  { value: "ablelynq", label: "AbleLynq" },
  { value: "deafwav", label: "DeafWav" },
  { value: "fullynq", label: "FulLynq" },
  { value: "jadesynq", label: "JadeSynq" },
  { value: "openlynq", label: "OpenLynq" },
  { value: "spiritsynq", label: "SpiritSynq" },
];

const APP_ROUTE: Record<string, string> = Object.fromEntries(
  APP_OPTIONS.map((a) => [a.value, `/${a.value}`]),
);

const PARTNER_TYPES = [
  { value: "creator", label: "Creator / influencer" },
  { value: "vendor", label: "Vendor (Wavlynq Store)" },
  { value: "mentor", label: "Mentor / coach" },
  { value: "press", label: "Press / media" },
  { value: "agency", label: "Agency / brand partner" },
  { value: "other", label: "Other" },
] as const;

type PartnerTypeValue = (typeof PARTNER_TYPES)[number]["value"];

const PartnerAccess = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [code, setCode] = useState(() => (params.get("code") ?? "").toUpperCase());
  const [name, setName] = useState("");
  const [appChoice, setAppChoice] = useState<string>("");
  const [busy, setBusy] = useState(false);

  // Apply form state
  const [partnerType, setPartnerType] = useState<PartnerTypeValue>("creator");
  const [primaryApp, setPrimaryApp] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const hasPrefilledCode = Boolean(params.get("code"));

  useEffect(() => {
    const c = params.get("code");
    if (c) setCode(c.toUpperCase());
  }, [params]);

  const handleRedeem = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (trimmed.length < 4) {
      toast.error("Enter your access code");
      return;
    }
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("redeem-influencer-code", {
        body: { code: trimmed, display_name: name || "Demo Reviewer" },
      });
      if (error) {
        let reason = error.message ?? "Could not redeem code";
        const context = (error as Error & { context?: Response }).context;
        if (context) {
          const payload = await context.clone().json().catch(() => null);
          reason = payload?.error ?? payload?.reason ?? payload?.detail ?? reason;
        }
        toast.error(reason.replaceAll("_", " "));
        return;
      }
      if (!data?.ok || !data?.session) {
        const reason = data?.error ?? "Code not valid";
        toast.error(reason.replaceAll("_", " "));
        return;
      }
      const { error: setErr } = await supabase.auth.setSession({
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token,
      });
      if (setErr) throw setErr;
      toast.success(`Welcome — you're in${data.label ? ` (${data.label})` : ""}.`);

      // Always auto-activate sandbox vendor and drop them straight into the
      // vendor portal — no extra URL param, no second page.
      try {
        const { error: actErr } = await supabase.functions.invoke(
          "store-vendor-sandbox-activate",
          { body: {} },
        );
        if (actErr) {
          console.warn("[partner-access] sandbox vendor activate failed", actErr);
          navigate("/store/vendor-setup", { replace: true });
          return;
        }
        navigate("/store/vendor", { replace: true });
      } catch (err) {
        console.warn("[partner-access] vendor activate threw", err);
        navigate("/store/vendor-setup", { replace: true });
      }
    } catch (err) {
      toast.error((err as Error).message ?? "Could not redeem code");
    } finally {
      setBusy(false);
    }
  };

  const handleApply = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting || submitted) return;
    const formEl = e.currentTarget;
    const fd = new FormData(formEl);
    setSubmitting(true);
    try {
      const payload = {
        full_name: String(fd.get("fullName") ?? "").trim(),
        email: String(fd.get("email") ?? "").trim().toLowerCase(),
        brand: String(fd.get("brand") ?? "").trim() || null,
        website: String(fd.get("website") ?? "").trim() || null,
        handle: String(fd.get("handle") ?? "").trim() || null,
        partner_type: partnerType,
        audience_size: String(fd.get("audience") ?? "").trim() || null,
        primary_app: primaryApp || null,
        pitch: String(fd.get("pitch") ?? "").trim(),
        source: "partner-access-page",
        submitter_user_id: user?.id ?? null,
      };
      if (!payload.full_name || !payload.email || !payload.pitch) {
        toast.error("Name, email, and pitch are required");
        setSubmitting(false);
        return;
      }

      // Check for recent duplicate submission (same email + type in last 24h)
      const { data: existing } = await supabase
        .from("partner_applications")
        .select("id")
        .eq("email", payload.email)
        .eq("partner_type", payload.partner_type)
        .gte("created_at", new Date(Date.now() - 86400000).toISOString())
        .limit(1);
      if (existing && existing.length > 0) {
        setSubmitted(true);
        toast.success("We already have your application — sit tight!");
        return;
      }

      const { data: inserted, error } = await supabase
        .from("partner_applications")
        .insert(payload)
        .select("id")
        .single();
      if (error) throw error;

      // Fire confirmation + admin email (non-blocking)
      supabase.functions
        .invoke("send-partner-application-email", {
          body: {
            fullName: payload.full_name,
            email: payload.email,
            partnerType: payload.partner_type,
            primaryApp: payload.primary_app,
            brand: payload.brand,
            pitch: payload.pitch,
            applicationId: inserted?.id,
          },
        })
        .then(({ error: fnErr }) => {
          if (fnErr) console.warn("[partner-email] edge fn error", fnErr);
        });

      setSubmitted(true);
      toast.success("Application received — we'll be in touch.");
      formEl.reset();
      setPartnerType("creator");
      setPrimaryApp("");
    } catch (err) {
      toast.error((err as Error).message ?? "Could not submit application");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <SiteHeader />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden border-b border-border/60">
          <div className="absolute inset-0 bg-wavlynq-gradient opacity-[0.08] pointer-events-none" />
          <div className="relative mx-auto w-full max-w-5xl px-6 py-20 text-center">
            <p className="text-xs font-medium uppercase tracking-[0.24em] text-primary">
              Creators · Vendors · Mentors · Press
            </p>
            <h1 className="mt-4 text-4xl md:text-6xl font-bold tracking-tight">
              Partner access
            </h1>
            <p className="mt-5 mx-auto max-w-2xl text-lg text-muted-foreground">
              Preview the Wavlynq ecosystem — all 9 sibling apps — with top-tier{" "}
              <span className="text-foreground font-medium">Attuned</span> access,
              or apply to partner with us as a creator, vendor, or mentor.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <a href="#redeem">
                <Button size="lg" className="font-semibold">
                  Have a code? Redeem it
                </Button>
              </a>
              <a href="#apply">
                <Button size="lg" variant="outline" className="font-semibold">
                  Apply to partner
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="mx-auto w-full max-w-5xl px-6 py-16">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-center">
            Why partner with Wavlynq?
          </h2>
          <p className="mt-3 text-center text-muted-foreground max-w-2xl mx-auto">
            Nine culturally-grounded apps for connection — friendship, mentorship,
            community, and meaningful matching. Help us shape it, or sell to it.
          </p>
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {[
              {
                Icon: Sparkles,
                title: "Full Attuned access",
                body: "Top-tier features across all 9 sibling apps for the duration of your code.",
              },
              {
                Icon: Zap,
                title: "Frictionless preview",
                body: "Drop into any app instantly to explore the experience your audience will see.",
              },
              {
                Icon: TrendingUp,
                title: "Custom promo codes",
                body: "Get a personal referral code so your audience saves and you earn rewards on signup.",
              },
              {
                Icon: Store,
                title: "Wavlynq Store",
                body: "Vendors can apply now to sell physical and digital goods to our communities.",
              },
              {
                Icon: Users,
                title: "Direct line to founders",
                body: "Your feedback shapes the roadmap. We read every note.",
              },
              {
                Icon: BadgeCheck,
                title: "Early access to what's next",
                body: "Preview new features, mentor tools, and Wavlynq Store before public rollout.",
              },
              {
                Icon: Megaphone,
                title: "Co-marketing",
                body: "Featured creator slots, joint launches, and content collaboration.",
              },
            ].map(({ Icon, title, body }) => (
              <Card key={title} className="p-6">
                <Icon className="h-6 w-6 text-primary" />
                <h3 className="mt-4 font-semibold">{title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{body}</p>
              </Card>
            ))}
          </div>
        </section>

        {/* Redeem code */}
        <section id="redeem" className="mx-auto w-full max-w-2xl px-6 py-12 scroll-mt-20">
          <div className="text-center mb-8">
            <p className="text-xs font-medium uppercase tracking-[0.24em] text-muted-foreground">
              Already have a code?
            </p>
            <h2 className="mt-3 text-2xl md:text-3xl font-bold tracking-tight">
              Redeem your access
            </h2>
          </div>

          <Card className="p-6">
            <form onSubmit={handleRedeem} className="space-y-4">
              <div>
                <Label htmlFor="code">Access code</Label>
                <Input
                  id="code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="e.g. K3Y4Z"
                  maxLength={8}
                  className="font-mono tracking-[0.3em] text-center text-lg uppercase mt-1"
                  autoComplete="off"
                />
              </div>
              <div>
                <Label htmlFor="name">What should we call you?</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your preferred name"
                  maxLength={80}
                  className="mt-1"
                  required
                />
              </div>
              <Button type="submit" disabled={busy || !name.trim()} className="w-full">
                {busy ? "Unlocking…" : "Enter the sandbox"}
              </Button>
            </form>

            <div className="mt-6 grid gap-3 text-xs text-muted-foreground sm:grid-cols-3">
              <div className="flex items-start gap-2">
                <Sparkles className="h-3.5 w-3.5 mt-0.5 text-primary" />
                <span>Full Attuned access with every feature unlocked</span>
              </div>
              <div className="flex items-start gap-2">
                <ShieldCheck className="h-3.5 w-3.5 mt-0.5 text-primary" />
                <span>Persistent account — log back in anytime</span>
              </div>
              <div className="flex items-start gap-2">
                <Lock className="h-3.5 w-3.5 mt-0.5 text-primary" />
                <span>All 13 à la carte features included</span>
              </div>
            </div>
          </Card>
        </section>

        {/* Apply to partner */}
        <section id="apply" className="mx-auto w-full max-w-2xl px-6 py-12 scroll-mt-20">
          <div className="text-center mb-8">
            <p className="text-xs font-medium uppercase tracking-[0.24em] text-muted-foreground">
              No code yet?
            </p>
            <h2 className="mt-3 text-2xl md:text-3xl font-bold tracking-tight">
              Apply to partner
            </h2>
            <p className="mt-3 text-muted-foreground">
              Creators, vendors, mentors, press — tell us about you and how
              you'd like to partner. We respond within 3 business days.
            </p>
          </div>

          <Card className="p-6">
            {submitted ? (
              <div className="text-center space-y-4 py-6">
                <BadgeCheck className="h-10 w-10 text-primary mx-auto" />
                <h3 className="text-lg font-semibold">Application received</h3>
                <p className="text-sm text-muted-foreground">
                  Thanks — we'll review and reply to the email you provided.
                </p>
                <Button variant="outline" onClick={() => setSubmitted(false)}>
                  Submit another
                </Button>
              </div>
            ) : (
              <form onSubmit={handleApply} className="space-y-4">
                <div>
                  <Label htmlFor="partnerType">I'm applying as</Label>
                  <Select
                    value={partnerType}
                    onValueChange={(v) => setPartnerType(v as PartnerTypeValue)}
                  >
                    <SelectTrigger id="partnerType" className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PARTNER_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {partnerType === "vendor" && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      Wavlynq Store launches post-V2. Vendor applications are
                      collected now and reviewed in waves.
                    </p>
                  )}
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="fullName">Full name</Label>
                    <Input id="fullName" name="fullName" required maxLength={120} className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" name="email" type="email" required maxLength={254} className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="brand">Brand / company (optional)</Label>
                    <Input id="brand" name="brand" maxLength={120} className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="website">Website (optional)</Label>
                    <Input id="website" name="website" placeholder="https://" maxLength={300} className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="handle">Primary handle</Label>
                    <Input id="handle" name="handle" placeholder="@yourhandle" maxLength={80} className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="audience">Audience size (optional)</Label>
                    <Input id="audience" name="audience" placeholder="e.g. 50K" maxLength={40} className="mt-1" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="primaryApp">Which app(s) excite you most?</Label>
                  <Select value={primaryApp} onValueChange={setPrimaryApp}>
                    <SelectTrigger id="primaryApp" className="mt-1">
                      <SelectValue placeholder="Pick one" />
                    </SelectTrigger>
                    <SelectContent>
                      {APP_OPTIONS.map((a) => (
                        <SelectItem key={a.value} value={a.label}>
                          {a.label}
                        </SelectItem>
                      ))}
                      <SelectItem value="All of them">All of them</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="pitch">How would you like to partner?</Label>
                  <Textarea
                    id="pitch"
                    name="pitch"
                    required
                    rows={5}
                    maxLength={4000}
                    placeholder={
                      partnerType === "vendor"
                        ? "What do you sell? Physical or digital? Audience fit, fulfillment, price points…"
                        : "Content ideas, community fit, what you'd want from us…"
                    }
                    className="mt-1"
                  />
                </div>

                <Button type="submit" disabled={submitting} className="w-full font-semibold">
                  {submitting ? "Sending…" : "Send application"}
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  Submissions are stored privately and reviewed by our team.
                </p>
              </form>
            )}
          </Card>
        </section>

        {/* Contact CTA */}
        <section className="border-t border-border/60 bg-muted/30">
          <div className="mx-auto w-full max-w-3xl px-6 py-16 text-center">
            <Mail className="h-8 w-8 text-primary mx-auto" />
            <h2 className="mt-4 text-2xl md:text-3xl font-bold tracking-tight">
              Prefer to talk directly?
            </h2>
            <p className="mt-3 text-muted-foreground">
              Reach the partnerships team at{" "}
              <a href="mailto:partners@wavlynq.com" className="font-bold text-primary hover:underline">
                partners@wavlynq.com
              </a>
              {" "}— we read every message personally.
            </p>
            <div className="mt-6">
              <a href="mailto:partners@wavlynq.com">
                <Button size="lg" variant="default" className="font-semibold">
                  Email partnerships
                </Button>
              </a>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
};

export default PartnerAccess;
